import React from 'react';
import { Box, CssBaseline, Snackbar, Alert, Typography, Button, Card, CardContent, TextField, Divider } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AppProvider, useApp } from './context/AppContext';
import { LanguageProvider } from './context/LanguageContext';
import API_BASE_URL from './config/api';

// Components & Views
import Sidebar from './components/Sidebar';
import BottomNavigationBar from './components/BottomNavigationBar';
import Header from './components/Header';
import DashboardView from './views/DashboardView';
import TablesView from './views/TablesView';
import POSTerminalView from './views/POSTerminalView';
import KDSView from './views/KDSView';
import InventoryView from './views/InventoryView';
import CaptainSimView from './views/CaptainSimView';
import SettingsView from './views/SettingsView';
import ReportsView from './views/ReportsView';
import AccountingView from './views/AccountingView';
import SubscriptionView from './views/SubscriptionView';
import OwnerSettingsView from './views/OwnerSettingsView';
import RolePermissionsView from './views/RolePermissionsView';
import LiveStatusView from './views/LiveStatusView';
import TokenDisplayView from './views/TokenDisplayView';
import QROrderingView from './views/QROrderingView';
import CustomerManagementView from './views/CustomerManagementView';
import CloudSaaSView from './views/CloudSaaSView';
import QROrderingCustomerView from './views/QROrderingCustomerView';
import QuickPOSTerminalView from './views/QuickPOSTerminalView';
import FranchiseHQView from './views/FranchiseHQView';
import PrinterUtility from './components/PrinterUtility';
import UtilityView from './views/UtilityView';
import DeveloperView from './views/DeveloperView';
import SaaSLandingView from './views/SaaSLandingView';
import TenantLoginView from './views/TenantLoginView';
import PwaInstallModal from './components/PwaInstallModal';
import DinexaBizView from './views/DinexaBizView';

import OwnerAppLauncherView from './views/OwnerAppLauncherView';

// Dinexa CURATED Theme logic is now inside AppContent


function AppContent() {
    const {
        activeView,
        setActiveView,
        currentRole,
        setCurrentRole,
        toast,
        closeToast,
        tenantSubdomain,
        setTenantSubdomain,
        isLoggedIn,
        setIsLoggedIn,
        setCurrentUser,
        allowedMenus,
        setAllowedMenus,
        setLockedActions,
        receiptTemplate,
        setReceiptTemplate,
        setSubscribedModules,
        isSubscriptionBlocked,
        subscriptionLockReason,
        setSidebarMobileOpen,
        themeMode
    } = useApp();

    React.useEffect(() => { 
        const urlParams = new URLSearchParams(window.location.search); 
        if (urlParams.get('app') === 'pos') {
            localStorage.removeItem('dinexa_app_mode');
        } else if (urlParams.get('app') === 'biz' || localStorage.getItem('dinexa_app_mode') === 'biz') { 
            localStorage.setItem('dinexa_app_mode', 'biz'); 
            setActiveView('biz'); 
        } 
    }, []); 
    
    const [deferredPrompt, setDeferredPrompt] = React.useState(null);
    const [showInstallPrompt, setShowInstallPrompt] = React.useState(false);
    const [updateAvailable, setUpdateAvailable] = React.useState(false);
    const [isCheckoutPanelOpen, setIsCheckoutPanelOpen] = React.useState(false);

    const dynamicTheme = React.useMemo(() => {
        let palette = {
            primary: { main: '#008751', dark: '#006f42', contrastText: '#ffffff' },
            secondary: { main: '#10b981', dark: '#059669', contrastText: '#ffffff' },
            background: { default: '#f8fafc', paper: '#ffffff' },
            mode: 'light',
            text: { primary: '#0f172a', secondary: '#475569' }
        };

        if (themeMode === 'dark') {
            palette = {
                primary: { main: '#10b981', dark: '#059669', contrastText: '#ffffff' },
                secondary: { main: '#34d399', dark: '#10b981', contrastText: '#0f172a' },
                background: { default: '#0f172a', paper: '#1e293b' },
                mode: 'dark',
                text: { primary: '#f8fafc', secondary: '#94a3b8' }
            };
        } else if (themeMode === 'solid') {
            palette = {
                primary: { main: '#ffffff', dark: '#e2e8f0', contrastText: '#0f766e' },
                secondary: { main: '#ccfbf1', dark: '#99f6e4', contrastText: '#0f766e' },
                background: { default: '#0f766e', paper: '#115e59' }, // Vibrant Teal Solid
                mode: 'dark', // Tells MUI to use dark text contrasts
                text: { primary: '#ffffff', secondary: '#ccfbf1' }
            };
        }

        return createTheme({
            palette,
            typography: {
                fontFamily: "'Outfit', 'Inter', 'Roboto', sans-serif",
                h4: { fontWeight: 800 },
                h5: { fontWeight: 800 },
                h6: { fontWeight: 800 },
                subtitle1: { fontWeight: 700 },
                subtitle2: { fontWeight: 700 },
                body1: { fontWeight: 500 },
                body2: { fontWeight: 500 },
                button: { fontWeight: 700, textTransform: 'none' }
            },
            shape: {
                borderRadius: 12
            },
            components: {
                MuiButton: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none'
                            }
                        }
                    }
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05)'
                        }
                    }
                }
            }
        });
    }, [themeMode]);

    React.useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShowInstallPrompt(true);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // Listen for Service Worker version updates
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                setUpdateAvailable(true);
            });
        }

        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to PWA install: ${outcome}`);
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
    }; const [devPin, setDevPin] = React.useState('');
    const [devError, setDevError] = React.useState('');
    const [devLoading, setDevLoading] = React.useState(false);

    const handleVerifyDevPin = async (e) => {
        if (e) e.preventDefault();
        setDevError('');
        setDevLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/developer/verify-pin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ pin: devPin })
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setCurrentRole('developer');
                setIsLoggedIn(true);
                localStorage.setItem('dinexa_is_logged_in', 'true');
                sessionStorage.setItem('dinexa_is_logged_in', 'true');
                setActiveView('developerDashboard');
                window.history.replaceState({}, document.title, '/');
            } else {
                setDevError(data.error || 'Incorrect developer PIN.');
            }
        } catch (err) {
            setDevError('Server error. Please try again.');
        } finally {
            setDevLoading(false);
        }
    };

    const urlParams = new URLSearchParams(window.location.search);
    const isDeveloperMode = urlParams.get('mode') === 'developer';

    if (!isLoggedIn && isDeveloperMode) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', p: 3 }}>
                <Card sx={{ maxWidth: 400, width: '100%', borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#0f172a' }}>
                            Developer Access Gate
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                            Please enter your secure Developer Access PIN to login.
                        </Typography>
                        <form onSubmit={handleVerifyDevPin}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    required
                                    fullWidth
                                    type="password"
                                    label="Developer PIN"
                                    size="small"
                                    value={devPin}
                                    onChange={(e) => setDevPin(e.target.value)}
                                    autoFocus
                                />
                                {devError && (
                                    <Alert severity="error" sx={{ borderRadius: 2 }}>
                                        {devError}
                                    </Alert>
                                )}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={devLoading}
                                    sx={{ fontWeight: 800, height: 40 }}
                                >
                                    {devLoading ? 'Verifying PIN...' : 'Verify & Enter'}
                                </Button>
                                <Button
                                    variant="text"
                                    fullWidth
                                    onClick={() => {
                                        window.location.href = '/';
                                    }}
                                    sx={{ color: '#64748b', fontWeight: 700 }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </form>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    if (isSubscriptionBlocked) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Card sx={{ maxWidth: 480, width: '100%', borderRadius: 4, textAlign: 'center', p: 2, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' }}>
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ width: 64, height: 64, bgcolor: '#fee2e2', color: '#dc2626', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                            <Typography variant="h4">🔒</Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#111827', mb: 1 }}>
                            Subscription Expiry Lock
                        </Typography>
                        <Alert severity="error" sx={{ width: '100%', mb: 3, fontWeight: 800, textAlign: 'left', borderRadius: 2 }}>
                            {subscriptionLockReason || 'तुमच्या प्लॅनची मुदत संपली आहे. बिलिंग सुरू ठेवण्यासाठी रिचार्ज करा!'}
                        </Alert>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 3, lineHeight: 1.5 }}>
                            सुरक्षेच्या कारणास्तव बिलिंग टर्मिनल तात्पुरते लॉक करण्यात आले आहे. कृपया इंटरनेट चालू करा किंवा आपल्या प्लॅनचे नूतनीकरण (Recharge) करा.
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={() => window.location.reload()}
                            sx={{ bgcolor: '#008751', color: '#ffffff', fontWeight: 800, py: 1.2, borderRadius: 2, '&:hover': { bgcolor: '#006f42' } }}
                        >
                            🔄 रिफ्रेश / पुन्हा प्रयत्न करा
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // Subdomain routing check: if not logged in, show Landing or Login screen
    if (!isLoggedIn) {
        if (tenantSubdomain) {
            return (
                <>
                    <TenantLoginView
                        subdomain={tenantSubdomain}
                        onLoginSuccess={(user) => {
                            setCurrentUser(user);
                            setIsLoggedIn(true);
                            localStorage.setItem('dinexa_is_logged_in', 'true');
                            sessionStorage.setItem('dinexa_is_logged_in', 'true');
                            localStorage.setItem('react_pos_current_user', JSON.stringify(user));
                            sessionStorage.setItem('react_pos_current_user', JSON.stringify(user));
                            const targetSub = user.subdomain || tenantSubdomain || localStorage.getItem('dinexa_active_tenant_subdomain');
                            if (targetSub) {
                                setTenantSubdomain(targetSub);
                                localStorage.setItem('dinexa_active_tenant_subdomain', targetSub);
                            }
                            if (user.role) {
                                setCurrentRole(user.role);
                                localStorage.setItem('react_pos_current_role', JSON.stringify(user.role));
                                sessionStorage.setItem('react_pos_current_role', JSON.stringify(user.role));
                            }
                            if (user.allowed_menus !== undefined) {
                                setAllowedMenus(user.allowed_menus);
                                localStorage.setItem('react_pos_allowed_menus', JSON.stringify(user.allowed_menus));
                                sessionStorage.setItem('react_pos_allowed_menus', JSON.stringify(user.allowed_menus));
                            }
                            if (user.locked_actions !== undefined) {
                                setLockedActions(user.locked_actions);
                                localStorage.setItem('react_pos_locked_actions', JSON.stringify(user.locked_actions));
                                sessionStorage.setItem('react_pos_locked_actions', JSON.stringify(user.locked_actions));
                            }
                            if (user.receipt_template) setReceiptTemplate(user.receipt_template);
                            if (user.gst_enabled !== undefined) {
                                const isGstOn = (user.gst_enabled === true || user.gst_enabled === 1 || user.gst_enabled === '1' || user.gst_enabled === 'true');
                                localStorage.setItem('dinexa_gst_enabled', isGstOn ? 'true' : 'false');
                            }
                            if (user.subscribed_modules !== undefined) {
                                setSubscribedModules(user.subscribed_modules);
                                localStorage.setItem('react_pos_subscribed_modules', JSON.stringify(user.subscribed_modules));
                            }
                            const urlParams = new URLSearchParams(window.location.search); 
                            const isBizMode = urlParams.get('app') === 'biz' || localStorage.getItem('dinexa_app_mode') === 'biz';
                            if (isBizMode && (user.role === 'owner' || user.role === 'admin')) { 
                                localStorage.setItem('dinexa_app_mode', 'biz'); 
                                setActiveView('biz'); 
                            } else if (user.role === 'owner' || user.role === 'admin') {
                                localStorage.removeItem('dinexa_app_mode');
                                setActiveView('appLauncher');
                            } else if (user.role === 'captain' || (user.allowed_menus && user.allowed_menus.includes('captain'))) {
                                localStorage.removeItem('dinexa_app_mode');
                                setActiveView('captain');
                            } else if (user.role === 'kitchen_staff' || (user.allowed_menus && user.allowed_menus.includes('kds'))) {
                                localStorage.removeItem('dinexa_app_mode');
                                setActiveView('kds');
                            } else if (user.role === 'billing_staff' || (user.allowed_menus && user.allowed_menus.includes('pos'))) {
                                localStorage.removeItem('dinexa_app_mode');
                                setActiveView(user.allowed_menus && user.allowed_menus.includes('tables') ? 'tables' : 'pos');
                            } else if (user.allowed_menus && Array.isArray(user.allowed_menus) && user.allowed_menus.length > 0) {
                                localStorage.removeItem('dinexa_app_mode');
                                if (user.allowed_menus.includes('tables')) setActiveView('tables');
                                else if (user.allowed_menus.includes('pos')) setActiveView('pos');
                                else if (user.allowed_menus.includes('quickPos')) setActiveView('quickPos');
                                else if (user.allowed_menus.includes('captain')) setActiveView('captain');
                                else if (user.allowed_menus.includes('kds')) setActiveView('kds');
                                else setActiveView(user.allowed_menus[0]);
                            } else { 
                                localStorage.removeItem('dinexa_app_mode');
                                setActiveView('tables'); 
                            }
                        }}
                        onBackToLanding={() => setTenantSubdomain('')}
                    />
                    <PwaInstallModal
                        open={showInstallPrompt || updateAvailable}
                        deferredPrompt={deferredPrompt}
                        onClose={() => setShowInstallPrompt(false)}
                        updateAvailable={updateAvailable}
                        onUpdateApp={() => window.location.reload()}
                    />
                </>
            );
        } else {
            return (
                <SaaSLandingView
                    onSelectTenant={(sub) => setTenantSubdomain(sub)}
                    onAccessDeveloper={() => {
                        setCurrentRole('developer');
                        setIsLoggedIn(true);
                        localStorage.setItem('dinexa_is_logged_in', 'true');
                        sessionStorage.setItem('dinexa_is_logged_in', 'true');
                        setActiveView('developerDashboard');
                    }}
                />
            );
        }
    }

    const params = new URLSearchParams(window.location.search);
    const isCustomerView = params.get('view') === 'menu';

    if (isCustomerView) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
                <QROrderingCustomerView />
                <Snackbar
                    open={toast.open}
                    autoHideDuration={4000}
                    onClose={closeToast}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={closeToast}
                        severity={toast.message.includes('⚠️') || toast.message.includes('Warning') ? 'warning' : 'success'}
                        sx={{ width: '100%', fontWeight: 700, borderRadius: 2 }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>
            </Box>
        );
    }

    const renderActiveView = () => {
        // SECURITY GUARD: Enforce allowed_menus permissions strictly for non-owner/developer roles
        let targetView = activeView;
        const systemAdminViews = ['ownerSettings', 'printerSettings', 'utility', 'rolePermissions', 'subscriptions', 'developerDashboard', 'appLauncher'];
        if (currentRole !== 'owner' && currentRole !== 'developer' && allowedMenus && Array.isArray(allowedMenus) && allowedMenus.length > 0 && !systemAdminViews.includes(targetView)) {
            if (!allowedMenus.includes(targetView)) {
                if (allowedMenus.includes('captain')) targetView = 'captain';
                else if (allowedMenus.includes('tables')) targetView = 'tables';
                else if (allowedMenus.includes('pos')) targetView = 'pos';
                else if (allowedMenus.includes('quickPos')) targetView = 'quickPos';
                else if (allowedMenus.includes('kds')) targetView = 'kds';
                else targetView = allowedMenus[0];
            }
        }

        // Never render Developer Admin Portal for non-developer roles
        if (targetView === 'developerDashboard' && currentRole !== 'developer') {
            if (allowedMenus && Array.isArray(allowedMenus)) {
                if (allowedMenus.includes('captain')) return <CaptainSimView />;
                if (allowedMenus.includes('tables')) return <TablesView />;
                if (allowedMenus.includes('pos')) return <POSTerminalView />;
            }
            return <POSTerminalView />;
        }

        // Never render Utility, Owner Settings, or Role Permissions for non-owner roles
        if ((targetView === 'utility' || targetView === 'ownerSettings' || targetView === 'rolePermissions') && currentRole !== 'owner' && currentRole !== 'developer') {
            if (allowedMenus && Array.isArray(allowedMenus)) {
                if (allowedMenus.includes('captain')) return <CaptainSimView />;
                if (allowedMenus.includes('tables')) return <TablesView />;
                if (allowedMenus.includes('pos')) return <POSTerminalView />;
            }
            return <POSTerminalView />;
        }

        switch (targetView) {
            case 'dashboard': 
                if (allowedMenus && Array.isArray(allowedMenus) && !allowedMenus.includes('dashboard')) {
                    if (allowedMenus.includes('captain')) return <CaptainSimView />;
                    if (allowedMenus.includes('tables')) return <TablesView />;
                    if (allowedMenus.includes('pos')) return <POSTerminalView />;
                    if (allowedMenus.includes('quickPos')) return <QuickPOSTerminalView />;
                    if (allowedMenus.includes('kds')) return <KDSView />;
                    return (allowedMenus.includes('tables')) ? <TablesView /> : <POSTerminalView />;
                }
                return <DashboardView />;
            case 'tables': return <TablesView />;
            case 'pos': return <POSTerminalView />;
            case 'quickPos': return <QuickPOSTerminalView />;
            case 'franchiseHq': return <FranchiseHQView />;
            case 'kds': return <KDSView />;
            case 'inventory': return <InventoryView />;
            case 'captain': return <CaptainSimView />;
            case 'reports': return <ReportsView />;
            case 'settings': return <ReportsView />;
            case 'printerSettings': return <PrinterUtility />;
            case 'accounting': return <AccountingView />;
            case 'subscriptions': return <SubscriptionView />;
            case 'ownerSettings': return <OwnerSettingsView />;
            case 'rolePermissions': return <RolePermissionsView />;
            case 'liveStatus': return <LiveStatusView />;
            case 'tokenDisplay': return <TokenDisplayView />;
            case 'qrOrdering': return <QROrderingView />;
            case 'crm': return <CustomerManagementView />;
            case 'cloudSaaS': return <CloudSaaSView />;
            case 'utility': return <UtilityView />;
            case 'developerDashboard': return <DeveloperView />;
            case 'appLauncher': return <OwnerAppLauncherView />;
            default: 
                if (allowedMenus && Array.isArray(allowedMenus)) {
                    if (allowedMenus.includes('captain')) return <CaptainSimView />;
                    if (allowedMenus.includes('tables')) return <TablesView />;
                    if (allowedMenus.includes('pos')) return <POSTerminalView />;
                }
                return <POSTerminalView />;
        }
    };

    if (activeView === 'biz') {
        return (
            <ThemeProvider theme={dynamicTheme}>
                <CssBaseline />
                <DinexaBizView />
            </ThemeProvider>
        );
    }
    
    if (activeView === 'appLauncher') {
        return (
            <ThemeProvider theme={dynamicTheme}>
                <CssBaseline />
                <OwnerAppLauncherView />
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={dynamicTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', bgcolor: 'background.default', pb: (isLoggedIn && activeView !== 'quickPos' && activeView !== 'pos' && activeView !== 'biz') ? '65px' : 0 }}>
                {/* Sidebar Navigation Drawer */}
                {isLoggedIn && <Sidebar />}

                {/* Main content pane */}
                <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
                    <Header />
                    <Box sx={{ flexGrow: 1, width: '100%', overflowY: activeView === 'quickPos' ? 'hidden' : 'auto', height: activeView === 'quickPos' ? 'calc(100vh - 64px)' : 'auto' }}>
                        {renderActiveView()}
                    </Box>
                </Box>

                {/* Fixed Bottom Navigation (Always shown on Table Floor and other views, hidden strictly on Quick POS, POS, and Biz) */}
                {isLoggedIn && activeView !== 'quickPos' && activeView !== 'pos' && activeView !== 'biz' && (
                    <BottomNavigationBar 
                        onMoreClick={() => setSidebarMobileOpen(true)}
                    />
                )}

                {/* PWA Centered Install Gate & Auto-Update Modal */}
            <PwaInstallModal
                open={showInstallPrompt}
                deferredPrompt={deferredPrompt}
                updateAvailable={updateAvailable}
                onClose={() => setShowInstallPrompt(false)}
                onUpdateApp={() => window.location.reload(true)}
            />

            {/* Global Prominent Top-Center Notification Banner */}
            <Snackbar
                open={toast.open}
                autoHideDuration={4500}
                onClose={closeToast}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ top: { xs: 16, sm: 24 } }}
            >
                {(() => {
                    const isError = toast.message.includes('❌') || toast.message.includes('Error') || toast.message.includes('Failed') || toast.message.includes('त्रुटी');
                    const isWarning = toast.message.includes('⚠️') || toast.message.includes('Warning') || toast.message.includes('अडचण');
                    return (
                        <Alert
                            onClose={closeToast}
                            severity={isError ? 'error' : (isWarning ? 'warning' : 'success')}
                            sx={{
                                width: '100%',
                                minWidth: { xs: 300, sm: 420 },
                                fontWeight: 900,
                                borderRadius: 3,
                                fontSize: '0.95rem',
                                boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.25)',
                                border: '2px solid',
                                borderColor: isError ? '#ef4444' : (isWarning ? '#f97316' : '#10b981'),
                                bgcolor: isError ? '#fef2f2' : (isWarning ? '#fff7ed' : '#ecfdf5'),
                                color: '#0f172a',
                                p: 1.5,
                                '& .MuiAlert-icon': {
                                    fontSize: 26,
                                    color: isError ? '#dc2626' : (isWarning ? '#ea580c' : '#059669')
                                }
                            }}
                        >
                            {toast.message}
                        </Alert>
                    );
                })()}
            </Snackbar>
            </Box>
        </ThemeProvider>
    );
}

export default function App() {
    return (
        <LanguageProvider>
            <AppProvider>
                <AppContent />
            </AppProvider>
        </LanguageProvider>
    );
}
